import React, { useState, useEffect, useRef } from 'react'

import LazyLoad from 'react-lazy-load'
import { useSelector, shallowEqual } from 'react-redux'
import { Redirect } from 'react-router-dom'
import styled from 'styled-components'

import { SimpleCarousel } from '.'
import { BreadCrumbs } from '..'
import { useEventListener, useWindowSize } from '../../_helpers'
import {
    Container,
    Meta,
    devices,
    colors,
    Number,
} from '../Common'
import { Loading } from '../Global/Loading'


const ImageCard = ({ image, openModal, thumbWidth, topPosition, index, showNumbers }) => {
    const [opacity, setOpacity] = useState(0)
    const height = (image.height / image.width) * thumbWidth

    return (
        <ImageContainer
            opacity={opacity}
            transform={topPosition}
            height={height}
            width={thumbWidth}
        >
            <LazyLoad
                height={height}
                once
                debounce={false}
                offsetVertical={1000}
                onContentVisible={() => setTimeout(() => setOpacity(1), 1000)}
            >
                <>
                    <GalleryImage src={image.image} alt={image.alt_text} onClick={openModal} />
                    <TextContainer>
                        {showNumbers && <ImageText>
                            <Number>{index + 1}</Number><br />
                            {image.description || 'Description'}
                        </ImageText>}
                    </TextContainer>
                </>
            </LazyLoad>
        </ImageContainer>
    )
}

const OldImageCard = ({ image, openModal, index, width, mobile, showNumbers }) => {
    const columns = mobile ? 2 : 3
    const thumbWidth = Math.round((width / columns) - 23)
    const [height, setHeight] = useState(0)
    const [opacity, setOpacity] = useState(0)

    let img = new Image()
    img.src = image.image
    img.onload = () => setHeight((img.height / img.width) * thumbWidth)

    useEffect(() => {
        if (height > 0) setTimeout(() => setOpacity(1), 500)
    }, [height])

    return (
        <OldImageContainer opacity={opacity} height={height} src={image.image} alt={image.alt_text} once>
            <TextContainer onClick={openModal}>
                {showNumbers && <ImageText>
                    <Number>{index + 1}</Number><br />
                    {image.description || 'Description'}
                </ImageText>}
            </TextContainer>
        </OldImageContainer>
    )
}


const PageView = () => {
    const { page, notFound } = useSelector(state => state.content, shallowEqual)
    const { gallery, title } = page

    const [showLightBox, setShowLightBox] = useState(false)
    const [activeSlide, setactiveSlide] = useState(0)
    const [topPosistions3, setTopPositions3] = useState([])
    const [topPosistions2, setTopPositions2] = useState([])
    const [height3, setHeight3] = useState(0)
    const [height2, setHeight2] = useState(0)
    const { width } = useWindowSize()
    const mobile = width < 480
    const container = useRef()
    const galleryContainer = useRef()
    const [containerWidth, setContainerWidth] = useState(0)
    const columns = mobile ? 2 : 3
    const thumbWidth = Math.round((containerWidth / columns) - 23)

    const openModal = i => {
        setactiveSlide(i)
        window.scrollTo(0, 0)
        document.getElementById('main').scrollTop = 0
        setShowLightBox(true)
    }

    const closeModal = () => setShowLightBox(false)
    useEffect(closeModal, [page])


    useEffect(() => {
        if (container) setContainerWidth(container.current.offsetWidth)
    }, [container])

    useEffect(() => {
        if (gallery) {
            let last_col = null
            const col3 = [0, 0, 0]
            const col2 = [0, 0]
            const border = 15
            const mobileBorder = 10
            const col3Positions = []
            const col2Positions = []
            const imageWidth = thumbWidth + border
            const mobileImageWidth = thumbWidth + mobileBorder

            for (let i = 0; i < gallery.length; i++) {
                const imageHeight = (gallery[i].height / gallery[i].width) * thumbWidth
                const yPos = border * 2 + imageHeight
                if (i >= gallery.length - 6) {
                    if (col3[0] <= col3[1] && col3[0] <= col3[2]) {
                        col3Positions.push(`translate3d(0px, ${col3[0]}px, 0px)`)
                        col3[0] += yPos
                    } else if (col3[1] <= col3[0] && col3[1] <= col3[2]) {
                        col3Positions.push(`translate3d(${imageWidth}px, ${col3[1]}px, 0px)`)
                        col3[1] += yPos
                    } else {
                        col3Positions.push(`translate3d(${imageWidth * 2}px, ${col3[2]}px, 0px)`)
                        col3[2] += yPos
                    }
                } else {
                    if (!last_col || last_col === 3) {
                        last_col = 1
                        col3Positions.push(`translate3d(0px, ${col3[0]}px, 0px)`)
                        col3[0] += yPos
                    } else if (last_col === 1) {
                        last_col = 2
                        col3Positions.push(`translate3d(${imageWidth}px, ${col3[1]}px, 0px)`)
                        col3[1] += yPos
                    } else {
                        last_col = 3
                        col3Positions.push(`translate3d(${imageWidth * 2}px, ${col3[2]}px, 0px)`)
                        col3[2] += yPos
                    }
                }

                const yPosMobile = mobileBorder * 2 + imageHeight
                if (i >= gallery.length - 4) {
                    if (col2[0] < col2[1]) {
                        col2Positions.push(`translate3d(0px, ${col2[0]}px, 0px)`)
                        col2[0] += yPosMobile
                    } else {
                        col2Positions.push(`translate3d(${mobileImageWidth}px, ${col2[1]}px, 0px)`)
                        col2[1] += yPosMobile
                    }
                } else {
                    if (i % 2 === 0) {
                        col2Positions.push(`translate3d(0px, ${col2[0]}px, 0px)`)
                        col2[0] += yPosMobile
                    } else {
                        col2Positions.push(`translate3d(${mobileImageWidth}px, ${col2[1]}px, 0px)`)
                        col2[1] += yPosMobile
                    }
                }
            }
            setHeight3(Math.max(...col3))
            setHeight2(Math.max(...col2))
            setTopPositions3(col3Positions)
            setTopPositions2(col2Positions)
        }
    }, [page, containerWidth])

    useEventListener('resize', () => setContainerWidth(container.current.offsetWidth))

    if (notFound) return <Redirect to='/' /> // this has to be after all hooks

    return (
        <>
            <Meta page={page} />
            <PageContainer ref={container} lightbox={showLightBox} height={columns === 3 ? height3 : height2}>
                {gallery && topPosistions3.length ?
                    showLightBox ?
                        <SimpleCarousel
                            slides={gallery}
                            close={closeModal}
                            activeImage={activeSlide}
                            title={title}
                            mobile={mobile}
                            showNumbers={page.url.includes('rentals')}
                        />
                        :
                        <>
                            {title && <BreadCrumbs title={title} mobile={mobile} />}
                            <OldImageList ref={galleryContainer}>
                                {gallery.map((image, i) =>
                                    <ImageCard
                                        key={i}
                                        index={i}
                                        image={image}
                                        thumbWidth={thumbWidth}
                                        openModal={() => openModal(i)}
                                        mobile={mobile}
                                        topPosition={mobile ? topPosistions2[i] : topPosistions3[i]}
                                        showNumbers={page.url.includes('rentals')}
                                    />
                                )}
                            </OldImageList>
                        </>
                    : <Loading marginbottom={30} />}
            </PageContainer>
        </>
    )
}

const PageContainer = styled(Container)`
    ${({ lightbox }) => lightbox && `
        padding-left: 0;
        padding-right: 0;
    `}

    height: ${({ height }) => height}px;

    min-height: calc(100vh - 90px);
    @media ${devices.small} {
        min-height: 95vh;
    }
`

const ImageList = styled.div`
    position: relative;
    width: 100%;
    display: flex;
    justify-content: space-between;
    flex-flow: row wrap;
`

const OldImageList = styled.div`
   column-count: 3;
   column-gap: 6px;

    @media ${devices.small} {
        column-count: 2;
        column-gap: 6px;
    }
`

const TextContainer = styled.div`
    height: 100%;
    cursor: pointer;
    background: transparent;
    color: ${colors.white};
    /* position: relative; */
    text-shadow: 0 1px 1px #000000;
`

const ImageText = styled.div`
    position: absolute;
    bottom: 0;
    padding: 14px;
    font-size: 13px;

    div {
        font-weight: 900;
        margin-bottom: 3px;
        font-size: 16px;
        @media ${devices.small} {
            font-size: 13px;
        }
    }

    @media ${devices.small} {
        font-size: 11px;
        padding: 7px;
    }

`

const ImageContainer = styled.div.attrs(({ height, width, transform, opacity }) => ({
    style: {
        height,
        width,
        transform,
        opacity,
    }
}))`
    transition: all 0.3s ease-in;
    left: 0px;
    position: absolute;
    flex: 0.3 1 32%;
`

const GalleryImage = styled.div.attrs(({ src }) => ({
    style: {
        background: `url(${src}) center/cover no-repeat`
    }
}))`
    height: 100%;
    border: 7px solid white;
    cursor: pointer;


    @media ${devices.small} {
        border-width: 3px;
        width: calc(100% - 9px);
    }
`

const OldImageContainer = styled(LazyLoad)`
    width: calc(100% - 18px);
    margin-bottom: 10px;
    transition: opacity 0.3s ease-in;
    opacity: ${props => props.opacity};
    border: 7px solid white;
    background: url(${props => props.src}) center/cover no-repeat;
    page-break-inside: avoid;
    break-inside: avoid;
    display: inline-block;

    @media ${devices.small} {
        border-width: 3px;
        width: calc(100% - 9px);
        /* margin-bottom: 5px; */
    }
`

export default PageView
